import React, { useState, useMemo, useCallback, useEffect, ReactElement, useRef } from 'react';
import { addMonths, addYears, addDays } from 'date-fns';
import {
  RibbonColumn,
  SortChangeEvent,
  SortItem,
  StackedRibbonList,
  StackedRibbonListHandles,
} from '@tanium/react-stacked-ribbon-list';
import { AdvancedFilters, AdvancedFilterInput, AdvancedFilterTextInput } from '@tanium/react-advanced-filters';
import { Page, PageHeader } from '@tanium/react-page';
import { Stack } from '@tanium/react-stack';
import { Well } from '@tanium/react-well';
import { Select, ValueType } from '@tanium/react-select';
import { DatePicker } from '@tanium/react-date-picker';
import { ActivityIcon } from '@tanium/react-graphics';
import { useTranslation } from 'react-i18next';
import { useEventHistory } from '../../hooks/useEventHistory';
import { DATE_TIME_TIMEZONE_FORMAT, formatDate, getDateFromDateOnlyString } from '../../utilities/dateHelper';
import { sortData, removeEmptyObjectValues } from '../../utilities/commonFuncs';
import { useWindowSize } from '../../hooks/useWindowSize';
import { AuditEvent, AuditHistoryEventBase } from '../../models/EventHistoryModels';
import { useAuthenticatedApiContext } from '../../api/useApiClient';

interface AdvancedFilterValues {
  event?: AuditEvent;
  user?: string;
  before?: string;
  after?: string;
}

interface Row {
  event: string;
  user: string;
  date: string;
  eventObject: AuditHistoryEventBase;
}

type SelectOption = { label: string; value: AuditEvent };

const MINIMUM_BATCH_SIZE = 30;

export const EventHistorySection: React.VFC = () => {
  const { t } = useTranslation();
  const { getEventTitle } = useEventHistory();
  const { height } = useWindowSize();

  const minDate = formatDate(addYears(Date.now(), -1));
  const today = formatDate(new Date());
  const lastMonth = formatDate(addMonths(Date.now(), -1));

  const [currentData, setCurrentData] = useState<AuditHistoryEventBase[]>([]);
  const [cursor, setCursor] = useState<string>('');
  const [totalCount, setTotalCount] = useState<number>(0);
  const [sortState, setSort] = useState<SortItem[]>([]);
  const [filterValues, setFilterValues] = useState<AdvancedFilterValues>({ before: today, after: lastMonth });

  const listRef = useRef<StackedRibbonListHandles>(null);
  const apiClient = useAuthenticatedApiContext();

  useEffect(() => {
    async function fetchData() {
      const data = await apiClient.getEventHistory({
        startTime: getDateFromDateOnlyString(filterValues.after!).getTime(),
        endTime: addDays(getDateFromDateOnlyString(filterValues.before!), 1).getTime(),
        limit: MINIMUM_BATCH_SIZE,
        cursor: undefined,
        user: filterValues.user,
        event: filterValues.event,
      });
      setCurrentData(data.data);
      setCursor(data.nextPageCursor);
      setTotalCount(data.totalCount);
    }
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    fetchData();
  }, [filterValues, apiClient]);

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
  const eventRows = useMemo(() => {
    const events = currentData.map((event): Row => {
      const username = event.username?.indexOf('@') === -1 ? event.email : event.username;
      return {
        event: getEventTitle(event.event),
        user: username || '',
        date: formatDate(new Date(event.createdAt), DATE_TIME_TIMEZONE_FORMAT),
        eventObject: event,
      };
    });

    return sortData(sortState, events);
  }, [currentData, sortState, getEventTitle]);

  const dataGetter = useCallback((index: number) => eventRows[index], [eventRows]);
  const itemIdGetter = useCallback(
    (index: number) => {
      const item = dataGetter(index);
      return item?.eventObject?.createdAt || `unknown-${index}`;
    },
    [dataGetter]
  );
  const isItemLoaded = useCallback((index: number) => !!dataGetter(index), [dataGetter]);

  /* istanbul ignore next */
  const loadMoreItems = useCallback(
    async (_: number, __: number) => {
      if (!cursor) {
        return;
      }
      const res = await apiClient.getEventHistory({
        startTime: getDateFromDateOnlyString(filterValues.after!).getTime(),
        endTime: addDays(getDateFromDateOnlyString(filterValues.before!), 1).getTime(),
        limit: MINIMUM_BATCH_SIZE,
        cursor: cursor || undefined,
        user: filterValues.user,
        event: filterValues.event,
      });
      setCurrentData(currentData?.concat(res.data));
      setCursor(res.nextPageCursor);
    },
    [currentData, cursor, filterValues, apiClient]
  );

  const resetData = () => {
    setCurrentData([]);
    setCursor('');
    setSort([]);
  };

  const columns: RibbonColumn<Row>[] = useMemo(() => {
    const ribbonColumns: RibbonColumn<Row>[] = [
      { label: t('Event'), dataKey: 'event', primary: true },
      { label: t('User'), dataKey: 'user' },
      { label: t('Date'), dataKey: 'date' },
    ];
    return ribbonColumns;
  }, [t]);

  const advancedFilters: JSX.Element = (
    <AdvancedFilters
      defaultCollapsed={false}
      onChange={(e) => {
        listRef.current?.scrollToTop();
        setFilterValues({ before: today, after: lastMonth, ...e.filterValues });
        resetData();
      }}
    >
      <AdvancedFilterInput label={t('Event')} dataKey="event">
        {({ updateFilter }): ReactElement => {
          return (
            <Select
              aria-label={t('Event')}
              options={Object.values(AuditEvent).map((auditEvent) => ({
                label: getEventTitle(auditEvent),
                value: auditEvent,
              }))}
              isMulti={false}
              value={filterValues?.event ? { label: getEventTitle(filterValues.event), value: filterValues.event } : ''}
              styles={{ container: (base) => ({ ...base, width: 200 }) }}
              onChange={(valueType: ValueType<SelectOption | string>, _action) => {
                const option = valueType as SelectOption;
                updateFilter({ displayValue: option.label, ...option });
              }}
            />
          );
        }}
      </AdvancedFilterInput>
      <AdvancedFilterTextInput label={t('User')} dataKey="user" />
      <AdvancedFilterInput label={t('From')} dataKey="after">
        {({ updateFilter, filterValue }): ReactElement => (
          <DatePicker
            aria-label={t('From')}
            value={(filterValue as string) || lastMonth}
            minDate={minDate}
            maxDate={filterValues.before}
            onChange={({ value }): void => {
              updateFilter({ value, displayValue: value });
            }}
          />
        )}
      </AdvancedFilterInput>
      <AdvancedFilterInput label={t('To')} dataKey="before">
        {({ updateFilter, filterValue }): ReactElement => (
          <DatePicker
            aria-label={t('To')}
            value={(filterValue as string) || today}
            minDate={filterValues.after}
            onChange={({ value }): void => {
              updateFilter({ value, displayValue: `Before: ${value}` });
            }}
          />
        )}
      </AdvancedFilterInput>
    </AdvancedFilters>
  );

  return (
    <Page header={<PageHeader icon={ActivityIcon} heading={'Event History'} />}>
      <Well p={1}>
        <Stack direction="column" itemSpacing={2}>
          <Well>
            <StackedRibbonList
              ref={listRef}
              advancedFilters={advancedFilters}
              sort={sortState}
              onSortChange={({ sort }: SortChangeEvent) => {
                setSort(sort);
              }}
              columns={columns}
              dataGetter={dataGetter}
              height={height - 400}
              isItemLoaded={isItemLoaded}
              itemCount={totalCount}
              itemIdGetter={itemIdGetter}
              itemsLabel={t('Events')}
              loadMoreItems={loadMoreItems}
              minimumBatchSize={MINIMUM_BATCH_SIZE}
              expandableContent={{
                renderer: ({ itemData }) => {
                  const eventContent = removeEmptyObjectValues(itemData.eventObject.details as Record<string, any>);
                  return (
                    <pre style={{ whiteSpace: 'pre-wrap' }} data-testid="test-expandable-content">
                      {JSON.stringify(eventContent, null, 2).replace(/": /g, '":')}
                    </pre>
                  );
                },
              }}
            />
          </Well>
        </Stack>
      </Well>
    </Page>
  );
};
